/**
 * HOW A RULE IS READ
 *
 *   Rule typed by the editor:   format=ondemand&lang=en | format=instructorled
 *
 *   "|" splits the rule into PARAM-groups.   Only ONE group has to match.   (OR)
 *   "&" splits a group into params.        EVERY param has to match.      (AND)
 *
 *   rule
 *   ├─ PARAM-group 1:   format=ondemand  AND  lang=en
 *   │       OR
 *   └─ PARAM-group 2:   format=instructorled
 *
 *   A rule with no "|" is simply a rule with one PARAM-group.
 */

const ShowWhenUrlQueryAttribute = "data-show-when-urlquery";
const ShowByDefaultAttribute = "data-show-when-urlquery-default";

/** One name=value from a query string, e.g. { name: "format", value: "ondemand" } */
export type QueryParam = { name: string; value: string };

/**
 * One or more params that must ALL be in the URL.
 * A group can hold a single param: "format=ondemand" is a group of one.
 * Params joined by "&" are in the SAME group; "|" starts a NEW group.
 */
export type ParamGroup = QueryParam[];

// #region Parsing (turning text into lists)

/**
 * Splits a query string on "&" into a list of params, trimmed and lowercased.
 * Used for both the URL and each AND-group, so both sides are cleaned the same way.
 *
 * "Format=OnDemand & lang=en" -> [{ name: "format", value: "ondemand" }, { name: "lang", value: "en" }]
 */
export function parseQueryParams(query: string): QueryParam[] {
    const params: QueryParam[] = [];

    // URLSearchParams splits on "&" and "=", decodes things like %20, and ignores a leading "?"
    new URLSearchParams(query.trim()).forEach((value, name) => {
        params.push({
            name: name.trim().toLowerCase(),
            value: value.trim().toLowerCase(),
        });
    });

    return params;
}

/**
 * Splits a rule on "|" into its PARAM-groups.
 *
 * "format=ondemand&lang=en | format=instructorled"
 *   -> [
 *        [{ format, ondemand }, { lang, en }],   // AND-group 1
 *        [{ format, instructorled }],            // AND-group 2
 *      ]
 */
export function parseRuleIntoParamGroups(rule: string): ParamGroup[] {
    const groupStrings = rule.split("|");
    const andGroups = groupStrings.map(parseQueryParams);

    // Drop empty groups left by a stray "|", e.g. "format=ondemand|".
    // An empty group has no params to fail, so it would match every URL.
    return andGroups.filter((andGroup) => andGroup.length > 0);
}

// #endregion

// #region Matching (comparing the rule to the URL)

/** Is this ONE param in the URL? */
export function urlHasParam(
    urlParams: QueryParam[],
    wantedParam: QueryParam,
): boolean {
    return urlParams.some(
        (urlParam) =>
            urlParam.name === wantedParam.name &&
            urlParam.value === wantedParam.value,
    );
}

/** AND: is EVERY param of this group in the URL? */
export function urlMatchesParamGroup(
    urlParams: QueryParam[],
    paramGroup: ParamGroup,
): boolean {
    return paramGroup.every((wantedParam) =>
        urlHasParam(urlParams, wantedParam),
    );
}

/** OR: does the URL match AT LEAST ONE of the rule's param groups? */
export function urlMatchesAtLeastOneParamGroup(
    rule: string,
    urlQuery: string = SaNavigationUtils.getCurrentPageUrl().search,
): boolean {
    const urlParams = parseQueryParams(urlQuery);
    const paramGroups = parseRuleIntoParamGroups(rule);

    return paramGroups.some((paramGroup) =>
        urlMatchesParamGroup(urlParams, paramGroup),
    );
}

/** Is a param with this NAME in the URL, whatever its value? */
export function urlHasParamName(
    urlParams: QueryParam[],
    name: string,
): boolean {
    return urlParams.some((urlParam) => urlParam.name === name);
}

export function urlHasAnyOfTheRuleParamNames(
    rule: string,
    urlQuery: string = SaNavigationUtils.getCurrentPageUrl().search,
): boolean {
    const urlParams = parseQueryParams(urlQuery);
    const ruleParams = parseRuleIntoParamGroups(rule).flat();

    return ruleParams.some((ruleParam) =>
        urlHasParamName(urlParams, ruleParam.name),
    );
}

// #endregion

export function checkQueryAndUpdateContainerDisplay(): void {
    $(`[${ShowWhenUrlQueryAttribute}]`).each(function () {
        const rule = this.getAttribute(ShowWhenUrlQueryAttribute);
        if (!rule) {
            return; // no rule: leave the container alone
        }
        const isDefaultContainerToShow =
            this.getAttribute(ShowByDefaultAttribute) === "true";

        const shouldShow =
            urlMatchesAtLeastOneParamGroup(rule) ||
            (isDefaultContainerToShow && !urlHasAnyOfTheRuleParamNames(rule));
        // "d-none" hides the container, so add it when the container should NOT show
        $(this).toggleClass("d-none", !shouldShow);
    });
}

export function onLoadCustomContainerWidget(): void {
    // initial page load
    checkQueryAndUpdateContainerDisplay();
    // every URL change after that
    $(window).on(
        SaNavigationUtils.UrlChangedEvent,
        checkQueryAndUpdateContainerDisplay,
    );
}

if (!SaNavigationUtils.NodeEnv.isTest()) {
    $(onLoadCustomContainerWidget);
}
